import Checkbox from "@/v2-ui/checkbox"
import Label from "@/v2-ui/label"
import { useLicenseProductsQuery } from "@/v2-backoffice-client/license/license.products.query"

export type LicenseAddOnProductsFieldProps = {
  /** The product codes picked so far. */
  value: number[]
  onValueChange(value: number[]): void
}

// The add-on editor's "Goes with" field: which product's license a customer
// must hold for the add-on to work. One checkbox per product, with the
// product's own sentence under it, so an operator picks "Placepoint MCP
// (Norway)" for the Sweden add-on without knowing what a product code is.
// More than one is allowed (Dataset write goes with the Dataset MCP license
// or the Norway MCP license, which includes the Dataset MCP).
function LicenseAddOnProductsField(props: LicenseAddOnProductsFieldProps) {
  const { value, onValueChange } = props

  const { data, loading } = useLicenseProductsQuery()
  const products = data?.getLicenseProducts ?? []

  return (
    <div className="space-y-2">
      <Label>Goes with</Label>
      <p className="text-xs text-muted-foreground">
        The license a customer must have for this add-on to work. The add-on
        then works for the people on that license while it is live, and stops
        with it. Pick at least one.
      </p>
      {loading && products.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading products...</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border">
          {products.map((product) => {
            const id = `add-on-product-${product.Code}`
            const isChecked = value.includes(product.Code)
            return (
              <li key={product.Code}>
                <label
                  htmlFor={id}
                  className="flex cursor-pointer items-start gap-3 px-3 py-2 text-sm hover:bg-accent/50"
                >
                  <Checkbox
                    id={id}
                    className="mt-0.5"
                    checked={isChecked}
                    data-feature-code="UI_BackofficeAddOn_Upsert_Product"
                    onCheckedChange={(checked) => {
                      onValueChange(
                        checked === true
                          ? [...value, product.Code]
                          : value.filter((code) => code !== product.Code),
                      )
                    }}
                  />
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="font-medium leading-none">
                      {product.Name} license
                    </span>
                    {product.Description && (
                      <span className="text-xs text-muted-foreground">
                        {product.Description}
                      </span>
                    )}
                  </span>
                </label>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default LicenseAddOnProductsField
